import { faCircleCheck, faCircleHalfStroke } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useModel } from "@stem-sims/nexus"
import Paginator from "components/Dashboards/General/Pagination/Paginator"
import MasteryBar from "components/Dashboards/General/Science35/MasteryBar"
import StandardsMasteryColorLengend from "components/Dashboards/General/StandardsMasteryColorLengend"
import ToolTip from "components/General/ToolTip"
import { useState } from "react"
import { Col, Row, Table } from "react-bootstrap"
import { BeatLoader } from "react-spinners"
import { scienceStudentMasteryData } from "shared/routes/science35"
import { getStudents } from "shared/routes/teacher"
import { StudentStandardMastery } from "shared/types/science35Types"
import { ClassResponse, StudentResponse } from "shared/types/teacherTypes"
import { useParams } from "react-router-dom"
import { toBlockType } from "helpers/blockStringConverter"

interface Props {
    activeClass: ClassResponse
}

const StudentMasteryPage = ({ activeClass }: Props) => {
    const { block } = useParams() as { block: string }

    const blockType = toBlockType(block)

    const [selectedStudent, setSelectedStudent] = useState<string>(null)

    const { response: data, loading } = useModel({
        model: scienceStudentMasteryData,
        props: { classId: activeClass.id, studentId: selectedStudent, targetBlock: blockType },
        deps: [blockType],
        onError: (err) => console.error(err),
    })

    const { response: students, loading: loadingStudents } = useModel({
        model: getStudents,
        props: { classID: activeClass.id },
        onError: (err) => console.error(err),
    })

    if (loadingStudents || !students) return <BeatLoader />

    const renderStudentRow = (student: StudentResponse, index: number) => {
        const alreadySelected = selectedStudent === student.uuid

        return (
            <tr key={index} className={`w-100 p-5 ${alreadySelected && "bg-info"}`} role="button" onClick={() => setSelectedStudent(alreadySelected ? null : student.uuid)}>
                <td className="text-primary">{student.name}</td>
            </tr>
        )
    }

    const renderStandardRow = (standard: StudentStandardMastery, index: number) => {
        const masteryResults = standard.masteryResults.slice(standard.masteryResults.length - 1)

        const initialResult = [{ mastery: standard.initialMasteryResult?.mastery, items: standard.initialMasteryResult?.submissionIds }]
        const results = masteryResults.map(m => ({ mastery: m.mastery, items: m.submissionIds }))

        return (
            <tr key={index} className="w-100 p-5 align-middle">
                <td className="text-primary"><ToolTip title={standard.standard?.description}><span>{standard.standard?.subtopicCode}</span></ToolTip></td>
                <td>
                    <div className="text-end">
                        <div>Knowledge&nbsp;Check</div>
                        <div>Recheck</div>
                    </div>
                </td>
                <td>
                    <MasteryBar total={1} results={initialResult} />
                    <div className="mt-2"></div>
                    <MasteryBar total={1} results={results} />
                </td>
                <td>{standard.recentGrade ? `${standard.recentGrade}%` : "N/A"}</td>
            </tr>
        )
    }

    const loadingTable = loading || !data

  return (
    <div className="mt-3">
        <h1><i className="fa-solid fa-user-graduate"></i> Student Mastery Page</h1>
        <Row className="mt-5">
            <Col xs={12} lg={3}>
                <Table bordered responsive>
                    <thead>
                        <tr>
                            <th>Students</th>
                        </tr>
                    </thead>
                    <tbody>
                        <Paginator items={students} renderItem={renderStudentRow} position="up" itemsPerPage={8} />
                    </tbody>
                </Table>
            </Col>
            <Col xs={12} lg={9}>
                {!selectedStudent && <h3 className="text-muted">Select a student to see their standard masteries.</h3>}
                {selectedStudent && loadingTable && <BeatLoader />}
                {selectedStudent && !loadingTable &&
                    <Table bordered responsive>
                        <thead>
                            <tr>
                                <th>Standard</th>
                                <th>Label</th>
                                <th>Mastery Results</th>
                                <th>Student Mastery</th>
                            </tr>
                        </thead>
                        <tbody>
                            <Paginator items={data.studentMasteries} renderItem={renderStandardRow} position="up" itemsPerPage={7} />
                        </tbody>
                    </Table>}
                {selectedStudent && <StandardsMasteryColorLengend />}
            </Col>
        </Row>
        {selectedStudent && !loadingTable && <Row className="mt-4 border border-1 border-info rounded p-4 mx-3">
            <Col>
                <FontAwesomeIcon 
                    role="button"
                    icon={faCircleCheck} 
                    className="flex-fill me-2 text-success"
                    size="lg"/>
                <span className="fw-bold">
                    <span className="me-1">Completion Progress: </span>
                    {data.completionProgress == null ? 'N/A' : `${data.completionProgress}%`}
                </span>
            </Col>
            <Col>
                <FontAwesomeIcon 
                    role="button"
                    icon={faCircleHalfStroke} 
                    className="flex-fill me-2"
                    size="lg"/>
                <span className="fw-bold">
                    <span className="me-1">Remediation Progress: </span>
                    {data.remediationProgress == null ? 'N/A' : `${data.remediationProgress}%`}
                </span>
            </Col>
        </Row>}
    </div>
  )
}

export default StudentMasteryPage
